import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useAuthState, useAuthDispatch } from "context";
import { apiFetch, formatUiErrors } from "utils";
import Button from "components/Button";
import FormField from "components/FormField";
import DatePickerField from "components/DatePickerField";
import ConfirmationModal from "components/ConfirmationModal";
import AlertModal from "components/AlertModal";
import Pagination from "components/Pagination";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const PAGE_SIZE = 25;

const Schema = Yup.object().shape({
  organization_id: Yup.number()
    .required("Organization ID is required")
    .positive()
    .integer(),
  month_year: Yup.string().required("Month/Year is required"),
});

const ShelterbuddyReprocess = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();

  const [previewStatus, setPreviewStatus] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewEvents, setPreviewEvents] = useState([]);
  const [previewOrg, setPreviewOrg] = useState(null);
  const [previewMonth, setPreviewMonth] = useState(null);
  const [previewYear, setPreviewYear] = useState(null);
  const [duplicateAnimalCount, setDuplicateAnimalCount] = useState(null);
  const [duplicateAnimals, setDuplicateAnimals] = useState([]);
  const [isExecuted, setIsExecuted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const [duplicatesPage, setDuplicatesPage] = useState(1);

  const pollingRef = useRef(null);
  const isMountedRef = useRef(true);
  const currentJobIdRef = useRef(null);
  const completedRef = useRef(false);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    currentJobIdRef.current = jobId;
    completedRef.current = false;

    const fetchStatus = async () => {
      if (completedRef.current) return;
      if (!isMountedRef.current || currentJobIdRef.current !== jobId) return;

      try {
        const res = await apiFetch({
          token,
          dispatch,
          method: "GET",
          endpoint: `/api/internal/admin/shelterbuddy_reprocess/preview_status/${jobId}`,
        });

        const data = res.data;
        if (!isMountedRef.current || currentJobIdRef.current !== jobId) return;

        if (data.status === "completed") {
          completedRef.current = true;
          stopPolling();
          setPreviewStatus("completed");
          setPreviewCount(data.count);
          setPreviewEvents(data.events || []);
          setDuplicateAnimalCount(data.duplicateAnimalCount ?? 0);
          setDuplicateAnimals(data.duplicateAnimals || []);
          setEventsPage(1);
          setDuplicatesPage(1);
          setIsLoadingPreview(false);
          toast.success(
            `Preview completed: ${data.count} animal event(s) found`
          );
        } else if (data.status === "failed") {
          completedRef.current = true;
          stopPolling();
          setPreviewStatus("failed");
          setIsLoadingPreview(false);
          toast.error(`Preview failed: ${data.error || "Unknown error"}`);
        } else {
          setPreviewStatus(data.status);
        }
      } catch {
        if (isMountedRef.current && currentJobIdRef.current === jobId) {
          completedRef.current = true;
          stopPolling();
          setPreviewStatus("failed");
          setIsLoadingPreview(false);
          toast.error("Error fetching preview status. Please try again.");
        }
      }
    };

    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 2000);
  };

  const handlePreview = async (values) => {
    setIsLoadingPreview(true);
    setPreviewStatus(null);
    setPreviewCount(null);
    setPreviewEvents([]);
    setDuplicateAnimalCount(null);
    setDuplicateAnimals([]);
    setIsExecuted(false);
    setEventsPage(1);
    setDuplicatesPage(1);

    const parsed = moment(values.month_year);
    const month = parsed.month() + 1;
    const year = parsed.year();

    setPreviewOrg(values.organization_id);
    setPreviewMonth(month);
    setPreviewYear(year);

    try {
      const res = await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/admin/shelterbuddy_reprocess/preview",
        body: {
          organization_id: values.organization_id,
          month,
          year,
        },
      });

      startPolling(res.data?.jobId);
    } catch (error) {
      toast.error(formatUiErrors(error));
      setIsLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    try {
      await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/admin/shelterbuddy_reprocess",
        body: {
          organization_id: previewOrg,
          month: previewMonth,
          year: previewYear,
        },
      });
      setIsExecuted(true);
      toast.success("Reprocess job queued successfully!");
    } catch (error) {
      toast.error(formatUiErrors(error));
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  const paginatedDuplicates = duplicateAnimals.slice(
    (duplicatesPage - 1) * PAGE_SIZE,
    duplicatesPage * PAGE_SIZE
  );
  const totalDuplicatesPages = Math.ceil(duplicateAnimals.length / PAGE_SIZE);

  const paginatedEvents = previewEvents.slice(
    (eventsPage - 1) * PAGE_SIZE,
    eventsPage * PAGE_SIZE
  );
  const totalEventsPages = Math.ceil(previewEvents.length / PAGE_SIZE);

  return (
    <div className="mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">
        ShelterBuddy — Reprocess Animal Events
      </h1>
      <p className="text-gray-600 mb-6">
        This tool removes <code>animal_events</code> records for a specific
        organization and month/year, allowing the data to be re-ingested
        correctly. Re-aggregation will be required after cleanup.
      </p>

      <div className="bg-white rounded p-6 border shadow-sm mb-6 max-w-xl">
        <Formik
          initialValues={{ organization_id: "", month_year: "" }}
          validationSchema={Schema}
          onSubmit={handlePreview}
        >
          {({ isValid, dirty }) => (
            <Form>
              <div className="mb-4">
                <FormField
                  name="organization_id"
                  label="Organization ID"
                  type="number"
                  placeholder="e.g. 3398"
                />
              </div>
              <div className="mb-6">
                <DatePickerField
                  label="Month/Year"
                  name="month_year"
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  className="form-input w-full"
                />
              </div>
              <Button
                type="submit"
                disabled={!isValid || !dirty || isLoadingPreview}
                isLoading={isLoadingPreview}
                loadingText="Loading..."
              >
                Preview
              </Button>
            </Form>
          )}
        </Formik>
      </div>

      {previewStatus === "completed" && !isExecuted && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-6 mb-6">
          <div className="flex items-start text-yellow-800 mb-4">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="mr-3 text-xl mt-1"
            />
            <div>
              <p className="font-bold text-lg mb-2">
                Preview — Organization {previewOrg}
              </p>
              <p className="text-sm mb-1">
                <strong>{previewCount}</strong> animal event(s) will be deleted
                for{" "}
                <strong>
                  {previewMonth}/{previewYear}
                </strong>{" "}
                and <strong>{duplicateAnimalCount}</strong> duplicate animal
                record(s) will be removed.
              </p>
              {previewCount > 0 && (
                <p className="text-sm font-semibold text-red-700 mt-3">
                  Deleting animal events is irreversible. Re-aggregation will be
                  required after cleanup.
                </p>
              )}
              {duplicateAnimalCount > 0 && (
                <p className="text-sm font-semibold text-red-700 mt-1">
                  Removing duplicate animal records is irreversible.
                </p>
              )}
            </div>
          </div>

          {duplicateAnimals.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <p className="text-sm font-semibold text-yellow-900 mb-2">
                Duplicate Animal Records to be Removed (
                {duplicateAnimals.length}) — showing{" "}
                {(duplicatesPage - 1) * PAGE_SIZE + 1}–
                {Math.min(duplicatesPage * PAGE_SIZE, duplicateAnimals.length)}
              </p>
              <table className="min-w-full text-xs border border-yellow-200 rounded">
                <thead className="bg-yellow-100 text-yellow-900">
                  <tr>
                    <th className="px-3 py-2 text-left">ID (duplicate)</th>
                    <th className="px-3 py-2 text-left">Animal ID</th>
                    <th className="px-3 py-2 text-left">Canonical ID (kept)</th>
                    <th className="px-3 py-2 text-left">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDuplicates.map((animal) => (
                    <tr
                      key={animal.id}
                      className="border-t border-yellow-200 odd:bg-white even:bg-yellow-50"
                    >
                      <td className="px-3 py-2">{animal.id}</td>
                      <td className="px-3 py-2">{animal.animalId}</td>
                      <td className="px-3 py-2">{animal.canonicalId}</td>
                      <td className="px-3 py-2">
                        {moment(animal.createdAt).format("YYYY-MM-DD HH:mm")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalDuplicatesPages > 1 && (
                <div className="flex justify-end mt-3">
                  <Pagination
                    totalPages={totalDuplicatesPages}
                    currentPage={duplicatesPage}
                    onPageChanged={(p) => setDuplicatesPage(p)}
                  />
                </div>
              )}
            </div>
          )}

          {previewEvents.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <p className="text-sm font-semibold text-yellow-900 mb-2">
                Animal Events to be Deleted ({previewEvents.length}) — showing{" "}
                {(eventsPage - 1) * PAGE_SIZE + 1}–
                {Math.min(eventsPage * PAGE_SIZE, previewEvents.length)}
              </p>
              <table className="min-w-full text-xs border border-yellow-200 rounded">
                <thead className="bg-yellow-100 text-yellow-900">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Animal ID</th>
                    <th className="px-3 py-2 text-left">Year</th>
                    <th className="px-3 py-2 text-left">Month</th>
                    <th className="px-3 py-2 text-left">Event Type</th>
                    <th className="px-3 py-2 text-left">Species</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEvents.map((event) => (
                    <tr
                      key={event.id}
                      className="border-t border-yellow-200 odd:bg-white even:bg-yellow-50"
                    >
                      <td className="px-3 py-2">{event.id}</td>
                      <td className="px-3 py-2">
                        {event.animalId ?? event.animal_id}
                      </td>
                      <td className="px-3 py-2">
                        {event.yearOfRecord ?? event.year_of_record}
                      </td>
                      <td className="px-3 py-2">
                        {event.monthOfRecord ?? event.month_of_record}
                      </td>
                      <td className="px-3 py-2">
                        {event.eventType ?? event.event_type}
                      </td>
                      <td className="px-3 py-2">{event.species}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalEventsPages > 1 && (
                <div className="flex justify-end mt-3">
                  <Pagination
                    totalPages={totalEventsPages}
                    currentPage={eventsPage}
                    onPageChanged={(p) => setEventsPage(p)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <Button
              onClick={() => setIsModalOpen(true)}
              disabled={previewCount === 0 && duplicateAnimalCount === 0}
            >
              Execute Reprocess for Org {previewOrg}
            </Button>
            {previewCount === 0 && duplicateAnimalCount === 0 && (
              <p className="text-sm text-green-700 mt-2">
                No animal events or duplicate animal records found for this
                organization/month/year.
              </p>
            )}
          </div>
        </div>
      )}

      {isExecuted && (
        <div className="bg-green-50 border border-green-200 rounded p-6">
          <div className="flex items-center text-green-700">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-xl" />
            <div>
              <p className="font-bold">Reprocess job queued successfully!</p>
              <p className="text-sm">
                {previewCount} animal event(s) will be deleted for org{" "}
                {previewOrg} — {previewMonth}/{previewYear}.
                {duplicateAnimalCount > 0 && (
                  <>
                    {" "}
                    {duplicateAnimalCount} duplicate animal record(s) removed.
                  </>
                )}{" "}
                Please re-aggregate after completion.
              </p>
            </div>
          </div>
        </div>
      )}

      {previewStatus === "failed" && (
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-sm text-red-700">
            Preview failed. Please try again.
          </p>
        </div>
      )}

      {isModalOpen && (
        <AlertModal
          isModalOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
        >
          <ConfirmationModal
            prompt={`Are you sure you want to delete ${previewCount} animal event(s) for org ${previewOrg} — ${previewMonth}/${previewYear}? This action is irreversible and re-aggregation will be required afterward.`}
            closeModal={() => setIsModalOpen(false)}
            confirmModal={() => {
              setIsModalOpen(false);
              handleExecute();
            }}
          />
        </AlertModal>
      )}
    </div>
  );
};

export default ShelterbuddyReprocess;
